// src/components/reporting/ReportDetailPage.js - Full report viewer
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import '../../styles/globals.css';
import './Analytics.css';

const ReportDetailPage = ({ reportId, onBack }) => {
  const { user } = useAuth();
  // GET /reports/:reportId is status-only (Brief A) — { status, ready }, no
  // title/scope/result/etc. `report` holds that minimal shape until status
  // flips to 'completed', at which point loadReport replaces it with the
  // full body from GET /reports/:reportId/body (Brief A.5) — this page has
  // no other data source, so nothing renders until that fetch lands.
  const [report, setReport] = useState(null);
  const [bodyFetchFailed, setBodyFetchFailed] = useState(false);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  const { loading, error, makeRequest } = useApiCall();

  useEffect(() => {
    const controller = new AbortController();
    loadReport(controller.signal);

    // Set up polling if report is not completed
    const pollInterval = setInterval(() => {
      if (report && (report.status === 'pending' || report.status === 'processing')) {
        loadReport(controller.signal);
      }
    }, 3000);

    return () => {
      clearInterval(pollInterval);
      controller.abort();
    };
  }, [reportId]);

  const loadReport = async (signal) => {
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/analytics/reports/${reportId}`, user, { signal }),
        report ? null : 'Loading report...' // Only show loading message on first load
      );

      if (!data.success || !data.data) return;

      if (data.data.status !== 'completed') {
        setReport(data.data); // minimal { status, ready } while still in progress
        return;
      }

      // Fire the body fetch once, on the transition into completed —
      // reusing the same abort signal so navigating away mid-fetch cancels it.
      try {
        const bodyResp = await apiService.get(`/api/v1/analytics/reports/${reportId}/body`, user, { signal });
        if (bodyResp?.success && bodyResp?.data) {
          setBodyFetchFailed(false);
          setReport(bodyResp.data);
        } else {
          setBodyFetchFailed(true);
          setReport({ status: 'completed' });
        }
      } catch (bodyErr) {
        if (bodyErr?.name === 'AbortError') return;
        console.error('Error loading report body:', bodyErr);
        setBodyFetchFailed(true);
        setReport({ status: 'completed' });
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error loading report:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      await makeRequest(
        () => apiService.delete(`/api/v1/analytics/reports/${reportId}`, user),
        'Deleting report...'
      );
      
      onBack();
    } catch (err) {
      console.error('Error deleting report:', err);
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'completed': return 'status-badge status-completed';
      case 'processing': return 'status-badge status-processing';
      case 'failed': return 'status-badge status-failed';
      default: return 'status-badge';
    }
  };

  const getTemplateBadgeClass = (reportType) => {
    switch(reportType) {
      case 'customer_intelligence': return 'template-badge template-customer-intelligence';
      case 'communication_skills': return 'template-badge template-communication-skills';
      default: return 'template-badge';
    }
  };

  const getTemplateLabel = (reportType) => {
    switch(reportType) {
      case 'customer_intelligence': return 'Customer Intelligence';
      case 'communication_skills': return 'Communication Skills';
      case 'ad_hoc': return 'Ad Hoc Analysis';
      default: return reportType;
    }
  };

  const renderStructuredData = (structuredData, reportType) => {
    if (!structuredData) return null;

    if (reportType === 'customer_intelligence') {
      return (
        <div className="structured-data">
          {/* Top Needs */}
          {structuredData.topNeeds && structuredData.topNeeds.length > 0 && (
            <div className="data-section">
              <h4>Top Customer Needs</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Need</th>
                    <th>Mentions</th>
                    <th>Sentiment</th>
                    <th>Example Quotes</th>
                  </tr>
                </thead>
                <tbody>
                  {structuredData.topNeeds.map((need, idx) => (
                    <tr key={idx}>
                      <td>{need.need}</td>
                      <td>{need.mentions}</td>
                      <td>
                        <span className={`sentiment-badge sentiment-${need.sentiment}`}>
                          {need.sentiment}
                        </span>
                      </td>
                      <td className="quotes-cell">
                        {need.quotes && need.quotes.slice(0, 2).map((quote, qIdx) => (
                          <div key={qIdx} className="quote-text">&quot;{quote}&quot;</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Buying Signals */}
          {structuredData.buyingSignals && structuredData.buyingSignals.length > 0 && (
            <div className="data-section">
              <h4>Buying Signals</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Signal</th>
                    <th>Count</th>
                    <th>Strength</th>
                    <th>Context</th>
                  </tr>
                </thead>
                <tbody>
                  {structuredData.buyingSignals.map((signal, idx) => (
                    <tr key={idx}>
                      <td>{signal.signal}</td>
                      <td>{signal.count}</td>
                      <td>
                        <span className={`strength-badge strength-${signal.strength}`}>
                          {signal.strength}
                        </span>
                      </td>
                      <td>{signal.context}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Objections */}
          {structuredData.objections && structuredData.objections.length > 0 && (
            <div className="data-section">
              <h4>Objections</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Objection</th>
                    <th>Frequency</th>
                    <th>Addressed</th>
                    <th>Resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {structuredData.objections.map((objection, idx) => (
                    <tr key={idx}>
                      <td>{objection.objection}</td>
                      <td>{objection.frequency}</td>
                      <td>
                        <span className={objection.addressed ? 'badge-success' : 'badge-warning'}>
                          {objection.addressed ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{objection.resolution || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Decision Makers */}
          {structuredData.decisionMakers && structuredData.decisionMakers.length > 0 && (
            <div className="data-section">
              <h4>Decision Makers</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Influence Level</th>
                    <th>Mentions</th>
                  </tr>
                </thead>
                <tbody>
                  {structuredData.decisionMakers.map((dm, idx) => (
                    <tr key={idx}>
                      <td>{dm.role}</td>
                      <td>
                        <span className={`influence-badge influence-${dm.influence}`}>
                          {dm.influence}
                        </span>
                      </td>
                      <td>{dm.mentioned}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }

    if (reportType === 'communication_skills') {
      return (
        <div className="structured-data">
          {/* Questioning Skills */}
          {structuredData.questioningSkills && (
            <div className="data-section">
              <h4>Questioning Skills</h4>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-label">Open Questions</div>
                  <div className="metric-value">{structuredData.questioningSkills.openQuestions}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Closed Questions</div>
                  <div className="metric-value">{structuredData.questioningSkills.closedQuestions}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Ratio (Open/Total)</div>
                  <div className="metric-value">
                    {(structuredData.questioningSkills.ratio * 100).toFixed(0)}%
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Quality</div>
                  <div className="metric-value">
                    <span className={`quality-badge quality-${structuredData.questioningSkills.quality}`}>
                      {structuredData.questioningSkills.quality}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Listening Skills */}
          {structuredData.listeningSkills && (
            <div className="data-section">
              <h4>Listening Skills</h4>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-label">Overall Score</div>
                  <div className="metric-value">{structuredData.listeningSkills.overallScore}/10</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Acknowledgment</div>
                  <div className="metric-value">{structuredData.listeningSkills.acknowledgmentFrequency}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Paraphrasing Examples</div>
                  <div className="metric-value">{structuredData.listeningSkills.paraphrasingExamples}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Building on Responses</div>
                  <div className="metric-value">
                    {structuredData.listeningSkills.buildingOnResponses ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Influence Patterns */}
          {structuredData.influencePatterns && structuredData.influencePatterns.length > 0 && (
            <div className="data-section">
              <h4>Influence Patterns</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pattern</th>
                    <th>Frequency</th>
                    <th>Effectiveness</th>
                  </tr>
                </thead>
                <tbody>
                  {structuredData.influencePatterns.map((pattern, idx) => (
                    <tr key={idx}>
                      <td className="pattern-name">{pattern.pattern}</td>
                      <td>{pattern.frequency}</td>
                      <td>{pattern.effectiveness}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Communication Clarity */}
          {structuredData.communicationClarity && (
            <div className="data-section">
              <h4>Communication Clarity</h4>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-label">Structure Score</div>
                  <div className="metric-value">{structuredData.communicationClarity.structureScore}/10</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Jargon Usage</div>
                  <div className="metric-value">{structuredData.communicationClarity.jargonUsage}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Message Consistency</div>
                  <div className="metric-value">{structuredData.communicationClarity.messageConsistency}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  if (loading && !report) {
    return (
      <div className="report-detail-loading">
        <div className="loading-dots mx-auto mb-3">
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
        </div>
        <p className="text-gray-600">Loading report...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="report-detail-error">
        <p>Report not found</p>
        <button onClick={onBack} className="btn btn-secondary">
          Back to Reports
        </button>
      </div>
    );
  }

  return (
    <div className="report-detail-page">
      {/* Header */}
      <div className="report-detail-header">
        <button onClick={onBack} className="btn btn-secondary">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Analytics
        </button>
        <button onClick={handleDelete} className="btn btn-danger">
          Delete
        </button>
      </div>

      <div className="report-detail-content">
        {/* Title and Badges — only present once the full body has loaded;
            the status-only poll response has no title/reportType/createdAt. */}
        {report.title != null && (
          <div className="report-header">
            <h2 className="report-title">{report.title}</h2>
            <div className="report-badges">
              <span className={getTemplateBadgeClass(report.reportType)}>
                {getTemplateLabel(report.reportType)}
              </span>
              <span className="report-created-date">
                {formatDateTime(report.createdAt)}
              </span>
              <span className={getStatusBadgeClass(report.status)}>
                {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
              </span>
            </div>
          </div>
        )}

        {/* Metadata Bar */}
        {report.title != null && (
          <div className="report-metadata">
            {/* Ad Hoc: show the user's analysis question */}
            {report.reportType === 'ad_hoc' && (report.userPrompt || report.prompt) && (
              <div className="metadata-item metadata-prompt">
                {report.userPrompt || report.prompt}
              </div>
            )}
            {/* Show conversation count for all report types */}
            {report.scope?.conversationIds?.length > 0 && (
              <div className="metadata-item metadata-prompt">
                {report.scope.conversationIds.length} conversation{report.scope.conversationIds.length !== 1 ? 's' : ''} analyzed
              </div>
            )}
          </div>
        )}

        {/* Processing State */}
        {(report.status === 'pending' || report.status === 'processing') && (
          <div className="report-processing">
            <div className="loading-spinner"></div>
            <p className="processing-text">Processing...</p>
            <p className="processing-subtext">This page will refresh automatically</p>
          </div>
        )}

        {/* Body Fetch Failed — the report succeeded; only the retrieval
            failed, so this is retryable, not rendered as a failed report. */}
        {report.status === 'completed' && bodyFetchFailed && (
          <div className="report-failed">
            <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3>Report Ready, But Couldn&apos;t Load</h3>
            <p className="error-message">
              The analysis finished successfully — only the retrieval failed. Try again below.
            </p>
            <button onClick={() => loadReport()} className="btn btn-secondary">
              Retry
            </button>
          </div>
        )}

        {/* Failed State */}
        {report.status === 'failed' && (
          <div className="report-failed">
            <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3>Report Generation Failed</h3>
            <p className="error-message">{report.error?.message || 'Unknown error occurred'}</p>
          </div>
        )}

        {/* Completed State */}
        {report.status === 'completed' && report.result && (
          <>
            {/* Executive Summary */}
            <section className="report-section">
              <h3 className="section-title">Executive Summary</h3>
              <div className="section-content">
                <p className="executive-summary">{report.result.executiveSummary}</p>
              </div>
            </section>

            {/* Key Findings */}
            <section className="report-section">
              <h3 className="section-title">Key Findings</h3>
              <div className="section-content">
                <pre className="formatted-text">{report.result.keyFindings}</pre>
              </div>
            </section>

            {/* Recommendations */}
            <section className="report-section">
              <h3 className="section-title">Recommendations</h3>
              <div className="section-content">
                <pre className="formatted-text">{report.result.recommendations}</pre>
              </div>
            </section>

            {/* Detailed Analysis */}
            {report.result.structuredData && (
              <section className="report-section collapsible">
                <button
                  onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
                  className="section-toggle"
                >
                  <h3 className="section-title">Detailed Analysis</h3>
                  <svg
                    className={`toggle-icon ${showDetailedAnalysis ? 'rotated' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showDetailedAnalysis && (
                  <div className="section-content">
                    {renderStructuredData(report.result.structuredData, report.reportType)}
                  </div>
                )}
              </section>
            )}

            {/* Technical Details */}
            <section className="report-section collapsible">
              <button
                onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                className="section-toggle"
              >
                <h3 className="section-title">Technical Details</h3>
                <svg
                  className={`toggle-icon ${showTechnicalDetails ? 'rotated' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showTechnicalDetails && report.metadata && (
                <div className="section-content">
                  <div className="technical-details">
                    <div className="detail-row">
                      <strong>Model:</strong> {report.metadata.model}
                    </div>
                    <div className="detail-row">
                      <strong>Messages Processed:</strong> {report.metadata.totalMessages}
                    </div>
                    {report.completedAt && report.startedAt && (
                      <div className="detail-row">
                        <strong>Duration:</strong>{' '}
                        {Math.round((new Date(report.completedAt) - new Date(report.startedAt)) / 1000)}s
                      </div>
                    )}
                    {report.metadata?.estimatedCost && (
                      <div className="detail-row">
                        <strong>Cost:</strong> ${report.metadata.estimatedCost.toFixed(4)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

ReportDetailPage.propTypes = {
  reportId: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired
};

export default ReportDetailPage;